/**
 * Test-only harness for the orders panels: fake WebSocket + fake /info fetch around the real providers.
 * Not imported by app code (no route); used by *.test.tsx here and handy for ad-hoc previews in tests.
 */
import { render } from '@testing-library/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { MemoryRouter } from 'react-router-dom';
import { HoldUserSession, UserSessionProvider } from '../UserSession';
import type { SessionTiming } from '../userSession';
import { EnvProvider } from '../../config/env';
import { HlProvider } from '../../hl/react';
import type { WebSocketLike } from '../../hl/ws';
import { GhostProvider } from '../../wallet/GhostProvider';
import { WalletProvider } from '../../wallet/WalletProvider';

export class FakeSocket implements WebSocketLike {
  static all: FakeSocket[] = [];
  readyState = 0;
  sent: string[] = [];
  onopen: ((ev: unknown) => void) | null = null;
  onclose: ((ev: unknown) => void) | null = null;
  onerror: ((ev: unknown) => void) | null = null;
  onmessage: ((ev: { data: unknown }) => void) | null = null;
  constructor(readonly url: string) {
    FakeSocket.all.push(this);
  }
  static last(): FakeSocket {
    const s = FakeSocket.all[FakeSocket.all.length - 1];
    if (!s) throw new Error('no socket yet');
    return s;
  }
  send(d: string) {
    this.sent.push(d);
  }
  close() {
    this.readyState = 3;
  }
  open() {
    this.readyState = 1;
    this.onopen?.({});
  }
  /** Server-side drop. */
  drop() {
    this.readyState = 3;
    this.onclose?.({});
  }
  push(channel: string, data: unknown) {
    this.onmessage?.({ data: JSON.stringify({ channel, data }) });
  }
  subscriptions(): Record<string, unknown>[] {
    return this.sent
      .map((s) => JSON.parse(s) as { method: string; subscription?: Record<string, unknown> })
      .filter((m) => m.method === 'subscribe' && m.subscription)
      .map((m) => m.subscription!);
  }
}

export type InfoHandler = (body: Record<string, unknown>) => unknown;

export interface FakeInfo {
  fetch: typeof fetch;
  calls: Record<string, unknown>[];
}

export function fakeInfo(handler: InfoHandler): FakeInfo {
  const calls: Record<string, unknown>[] = [];
  const f = (async (_url: unknown, init?: { body?: unknown }) => {
    const body = JSON.parse(String(init?.body)) as Record<string, unknown>;
    calls.push(body);
    const out = handler(body);
    return new Response(JSON.stringify(out ?? null), { status: 200, headers: { 'content-type': 'application/json' } });
  }) as unknown as typeof fetch;
  return { fetch: f, calls };
}

export interface OrdersSession {
  /** Hold this user's session instead of following the wallet and ?ghost=. */
  user?: string;
  /** With `user`: as the connected wallet. */
  own?: boolean;
  timing?: SessionTiming;
}

/** REST answers at once in these tests, as most of them have no stream frames. Session tests set their own timing. */
export const INSTANT_SESSION: SessionTiming = { fallbackMs: 0 };

export function renderOrders(ui: ReactNode, info: FakeInfo, path = '/', session: OrdersSession = {}) {
  FakeSocket.all = [];
  const qc = new QueryClient({ defaultOptions: { queries: { retry: false } } });
  return render(
    <MemoryRouter initialEntries={[path]} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <EnvProvider initial="mainnet">
        <QueryClientProvider client={qc}>
          <HlProvider
            infoOptions={{ fetch: info.fetch, maxRetries: 0 }}
            wsOptions={{ createSocket: (u) => new FakeSocket(u), backoffBaseMs: 1, backoffMaxMs: 2, random: () => 0 }}
          >
            <WalletProvider>
              <GhostProvider>
                {session.user ? (
                  <HoldUserSession user={session.user} own={session.own ?? false} timing={session.timing ?? INSTANT_SESSION}>
                    {ui}
                  </HoldUserSession>
                ) : (
                  <UserSessionProvider timing={session.timing ?? INSTANT_SESSION}>{ui}</UserSessionProvider>
                )}
              </GhostProvider>
            </WalletProvider>
          </HlProvider>
        </QueryClientProvider>
      </EnvProvider>
    </MemoryRouter>,
  );
}
